import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";

export const ADMIN_USERS_KEY = ["admin", "users"] as const;

// ---- Query ----

export function useAdminUsersQuery(opts?: {
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, opts],
    queryFn: async () => {
      const res = await authClient.admin.listUsers({
        query: {
          searchValue: opts?.search,
          searchField: opts?.search ? ("email" as const) : undefined,
          limit: opts?.limit ?? 50,
          offset: opts?.offset ?? 0,
        },
      });
      if (res.error) throw new Error(res.error.message ?? "Errore lista utenti");
      return res.data;
    },
  });
}

// ---- Mutations ----

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
}

export function useAdminCreateUser() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      name: string;
      role: "user" | "admin";
    }) => {
      const res = await authClient.admin.createUser({
        email: input.email,
        password: input.password,
        name: input.name,
        role: input.role,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore creazione utente");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente creato");
      void invalidate();
    },
    onError: (e) => toast.error(`Creazione fallita: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminUpdateUser() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { userId: string; data: Record<string, unknown> }) => {
      const res = await authClient.admin.updateUser({
        userId: input.userId,
        data: input.data,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore aggiornamento");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente aggiornato");
      void invalidate();
    },
    onError: (e) => toast.error(`Aggiornamento fallito: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminSetRole() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { userId: string; role: "user" | "admin" }) => {
      const res = await authClient.admin.setRole({
        userId: input.userId,
        role: input.role,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore cambio ruolo");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Ruolo aggiornato");
      void invalidate();
    },
    onError: (e) => toast.error(`Cambio ruolo fallito: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminSetPassword() {
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const res = await authClient.admin.setUserPassword({
        userId: input.userId,
        newPassword: input.newPassword,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore reset password");
      return res.data;
    },
    onSuccess: () => toast.success("Password aggiornata"),
    onError: (e) => toast.error(`Reset password fallito: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminBanUser() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { userId: string; banReason?: string }) => {
      const res = await authClient.admin.banUser({
        userId: input.userId,
        banReason: input.banReason,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore ban");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente bannato");
      void invalidate();
    },
    onError: (e) => toast.error(`Ban fallito: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminUnbanUser() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await authClient.admin.unbanUser({ userId });
      if (res.error) throw new Error(res.error.message ?? "Errore unban");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente riabilitato");
      void invalidate();
    },
    onError: (e) => toast.error(`Unban fallito: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useAdminRemoveUser() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await authClient.admin.removeUser({ userId });
      if (res.error) throw new Error(res.error.message ?? "Errore rimozione");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente rimosso");
      void invalidate();
    },
    onError: (e) => toast.error(`Rimozione fallita: ${e instanceof Error ? e.message : String(e)}`),
  });
}
